import { Globe } from 'lucide-react';
import { LOCALES, LOCALE_NAMES, useI18n, useT } from '@/i18n';
import type { Locale } from '@/i18n';
import { cn } from '@/lib/cn';

/**
 * Language picker.
 *
 * A native <select> on purpose: it gets the platform's own picker on mobile,
 * full keyboard support for free, and no focus-trapping to get wrong. Options
 * are labelled with endonyms, so someone who landed in the wrong language can
 * still recognise their own.
 */
export function LanguagePicker({ className }: { className?: string }) {
  const locale = useI18n((state) => state.locale);
  const setLocale = useI18n((state) => state.setLocale);
  const t = useT();

  return (
    <div className={cn('relative inline-flex items-center', className)}>
      <Globe
        aria-hidden="true"
        className="pointer-events-none absolute left-2 size-4 text-muted"
        strokeWidth={1.75}
      />
      <select
        value={locale}
        onChange={(event) => setLocale(event.target.value as Locale)}
        aria-label={t('language.change')}
        className={cn(
          'h-9 cursor-pointer appearance-none rounded-sm bg-transparent',
          'py-0 pl-7 pr-2 text-sm text-muted',
          'transition-colors hover:text-ink focus-visible:text-ink',
        )}
      >
        {LOCALES.map((option) => (
          <option key={option} value={option}>
            {LOCALE_NAMES[option]}
          </option>
        ))}
      </select>
    </div>
  );
}

/** Expanded variant for the settings screen. */
export function LanguageSetting() {
  const locale = useI18n((state) => state.locale);
  const setLocale = useI18n((state) => state.setLocale);
  const t = useT();

  return (
    <div className="flex flex-col gap-2">
      <span className="field-label">{t('language.label')}</span>
      <div role="radiogroup" aria-label={t('language.label')} className="flex flex-wrap gap-2">
        {LOCALES.map((option) => {
          const selected = option === locale;
          return (
            <button
              key={option}
              type="button"
              role="radio"
              aria-checked={selected}
              // The label is already in its own language; announcing it in the
              // current one would be wrong, so mark it up as such.
              lang={option}
              onClick={() => setLocale(option)}
              className={cn(
                'h-11 rounded-md border px-4 text-sm transition-colors duration-fast ease-out',
                selected
                  ? 'border-accent-graphic bg-accent-soft text-ink'
                  : 'border-hairline-strong text-muted hover:text-ink',
              )}
            >
              {LOCALE_NAMES[option]}
            </button>
          );
        })}
      </div>
    </div>
  );
}
